<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

import ContactFollowUpsAPI from 'dashboard/api/contactFollowUps';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import FollowUpEnrollDialog from 'dashboard/components-next/Contacts/FollowUpEnrollDialog/FollowUpEnrollDialog.vue';

const props = defineProps({
  contactId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();

const enrollments = ref([]);
const isFetching = ref(false);

const followUpState = useMapGetter('conversationFollowUps/getState');

// Identity of the newest fetch: a slower response for a previously shown
// contact must never fill this contact's list.
let fetchGeneration = 0;

const fetchEnrollments = async () => {
  fetchGeneration += 1;
  const generation = fetchGeneration;
  isFetching.value = true;
  try {
    const response = await ContactFollowUpsAPI.getEnrollments(props.contactId);
    if (generation !== fetchGeneration) return;
    enrollments.value = response.data.enrollments || [];
  } catch {
    if (generation === fetchGeneration)
      useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.LOAD_ERROR'));
  } finally {
    if (generation === fetchGeneration) isFetching.value = false;
  }
};

// Same consistency signals as the conversation panel: cable events about any
// cycle invalidate the tab (a send/conclusion/cancellation by another actor
// may belong to one of the listed conversations), and a websocket reconnect
// self-heals events lost while offline.
watch(
  () => followUpState.value.liveEvent,
  () => fetchEnrollments()
);
onMounted(() => {
  fetchEnrollments();
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchEnrollments);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, fetchEnrollments);
});
onBeforeUnmount(() => {
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, fetchEnrollments);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED, fetchEnrollments);
});

const isInProgress = enrollment =>
  ['active', 'paused'].includes(enrollment.status);

const statusLabel = enrollment => {
  switch (enrollment.status) {
    case 'active':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.ACTIVE');
    case 'paused':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.PAUSED');
    case 'completed':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.COMPLETED');
    case 'cancelled':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.CANCELLED');
    case 'failed':
      return t('CONVERSATION_SIDEBAR.FOLLOW_UP.STATUS.FAILED');
    default:
      return '';
  }
};

// Management reuses the conversation-scoped endpoints and store guards: the
// operation is keyed by the enrollment's conversation and bound to the
// displayed cycle's id (a stale card conflicts server-side). Managing state
// is a per-conversation list, so acting on one card never re-enables another
// still in flight.
const isManaging = enrollment =>
  followUpState.value.managingConversationIds.includes(
    enrollment.conversation_display_id
  );

// Returns whether the operation succeeded; the success message is the
// caller's, chosen AFTER the refreshed list is in (resume may cancel).
const manage = async (enrollment, operation) => {
  try {
    await store.dispatch('conversationFollowUps/manage', {
      conversationId: enrollment.conversation_display_id,
      enrollmentId: enrollment.id,
      operation,
    });
    return true;
  } catch (error) {
    const message =
      error?.response?.data?.error ||
      t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.ERROR');
    useAlert(message);
    return false;
  } finally {
    await fetchEnrollments();
  }
};

const pauseCycle = async enrollment => {
  if (await manage(enrollment, 'pause'))
    useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.PAUSE_SUCCESS'));
};
// Resuming a cycle whose cancellation cause accrued while paused ends it
// instead — say so rather than announcing a resume that did not happen.
const resumeCycle = async enrollment => {
  if (!(await manage(enrollment, 'resume'))) return;
  const refreshed = enrollments.value.find(item => item.id === enrollment.id);
  if (refreshed?.status === 'cancelled') {
    useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME_CANCELLED'));
  } else {
    useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME_SUCCESS'));
  }
};

const removeDialogRef = ref(null);
const enrollmentPendingRemoval = ref(null);
watch(
  () => props.contactId,
  () => {
    enrollments.value = [];
    enrollmentPendingRemoval.value = null;
    removeDialogRef.value?.close();
    fetchEnrollments();
  }
);
const requestRemove = enrollment => {
  enrollmentPendingRemoval.value = enrollment;
  nextTick(() => removeDialogRef.value?.open());
};
const confirmRemove = async () => {
  const enrollment = enrollmentPendingRemoval.value;
  removeDialogRef.value?.close();
  enrollmentPendingRemoval.value = null;
  if (!enrollment) return;
  if (await manage(enrollment, 'remove'))
    useAlert(t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_SUCCESS'));
};

const enrollDialogRef = ref(null);
const enrollContactIds = computed(() => [Number(props.contactId)]);
const openEnrollDialog = () => enrollDialogRef.value?.open();
</script>

<template>
  <div class="flex flex-col gap-3 px-6 py-4 text-sm text-n-slate-12">
    <div class="flex items-center justify-between">
      <Button
        size="sm"
        color="blue"
        icon="i-lucide-alarm-clock-plus"
        :label="t('CONTACTS_LAYOUT.CARD.ADD_TO_FOLLOW_UP')"
        @click="openEnrollDialog"
      />
    </div>

    <div v-if="isFetching" class="flex justify-center py-6">
      <Spinner class="size-5" />
    </div>

    <p v-else-if="!enrollments.length" class="text-n-slate-11">
      {{ t('CONTACTS_LAYOUT.FOLLOW_UP_TAB.EMPTY') }}
    </p>

    <div v-else class="flex flex-col gap-3">
      <div
        v-for="enrollment in enrollments"
        :key="enrollment.id"
        class="flex flex-col gap-2 p-3 border border-n-weak rounded-lg"
      >
        <div class="flex items-center justify-between gap-2">
          <span class="font-medium truncate">{{ enrollment.flow_name }}</span>
          <span
            class="shrink-0 text-xs font-medium px-2 py-0.5 rounded-full"
            :class="
              enrollment.status === 'active'
                ? 'bg-n-teal-3 text-n-teal-11'
                : enrollment.status === 'paused'
                  ? 'bg-n-amber-3 text-n-amber-11'
                  : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{ statusLabel(enrollment) }}
          </span>
        </div>
        <p class="text-xs text-n-slate-11">
          {{
            t('CONTACTS_LAYOUT.FOLLOW_UP_TAB.CONVERSATION', {
              id: enrollment.conversation_display_id,
              inbox: enrollment.inbox_name,
            })
          }}
        </p>
        <p v-if="enrollment.next_step" class="text-xs text-n-slate-11">
          {{
            t('CONVERSATION_SIDEBAR.FOLLOW_UP.SENT_COUNT', {
              count: enrollment.sent_steps,
              total: enrollment.total_steps,
            })
          }}
        </p>

        <div v-if="isInProgress(enrollment)" class="flex items-center gap-2">
          <Button
            v-if="enrollment.status === 'active'"
            size="sm"
            variant="faded"
            color="slate"
            icon="i-lucide-pause"
            :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.PAUSE')"
            :disabled="isManaging(enrollment)"
            @click="pauseCycle(enrollment)"
          />
          <Button
            v-else
            size="sm"
            variant="faded"
            color="teal"
            icon="i-lucide-play"
            :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.RESUME')"
            :disabled="isManaging(enrollment)"
            @click="resumeCycle(enrollment)"
          />
          <Button
            size="sm"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash-2"
            :label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE')"
            :disabled="isManaging(enrollment)"
            @click="requestRemove(enrollment)"
          />
        </div>
      </div>
    </div>

    <Dialog
      v-if="enrollmentPendingRemoval"
      ref="removeDialogRef"
      type="alert"
      :title="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.TITLE')"
      :description="
        t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE_CONFIRM.DESCRIPTION')
      "
      :confirm-button-label="t('CONVERSATION_SIDEBAR.FOLLOW_UP.MANAGE.REMOVE')"
      @confirm="confirmRemove"
      @close="enrollmentPendingRemoval = null"
    />

    <FollowUpEnrollDialog
      ref="enrollDialogRef"
      :contact-ids="enrollContactIds"
      @completed="fetchEnrollments"
    />
  </div>
</template>
